<template>
  <PageView
    :application="application"
    class="with-submenu"
  >
    <SubMenu
      :aria-id="$t('menu.aria-sub-menu')"
      :paths="subMenuPaths"
      :root="application.localName || application.title || applicationName"
      role="navigation"
    />
    <TitleAndDescription
      :application="application"
      :local-title="
        type[type.length - 1] === 'dataTypes'
          ? $t('titles.data-types-page', {
            applicationName: application.localName || application.title || applicationName,
          })
          : $t('titles.references-page', {
            applicationName: application.localName || application.title || applicationName,
          })
      "
      :local-description="application.localDescription"
      :local-name="application.localName"
      :with-auth="true"
      :can-create-application="canCreateApplication"
      :can-manager-application="canManagerApplication"
      :can-manager-user="canManagerUser"
      :application-name="applicationName"
    />
    <div class="contentView">
      <div
        v-if="errorsMessages.length !== 0"
        style="margin: 10px"
      >
        <ShowErrors
          :errors-messages="errorsMessages"
          :errors-messages-length="errorsMessages.length"
          :title="$t('message.data-type-config-error')"
        />
      </div>
      <div
        v-if="hasTags"
        id="tagsCollapse"
        class="column"
      >
        <TagsCollapse
          :tags="tags"
          @change:tag="changeTagSelected($event)"
          @change:all-tags="changeAllValueTags($event)"
        />
      </div>
      <LoadingAnimate
        v-if="isLoading"
        :size="'is-small'"
      />
      <div
        v-else-if="Object.keys(datasToBeShown).length>0"
        class="section"
      >
        <CollapsibleTree
          v-for="(data, i) in datasToBeShown"
          :id="i + 1"
          :key="data.id"
          :application-name="applicationName"
          :application-title="$t('titles.references-page', { applicationName: applicationName })"
          :buttons="buttons(data)"
          :build-button="buttons"
          :is-loading="csvRefIsLoading[data.id]"
          :is-uploading="isUploading"
          :level="0"
          :line-count="lineCountSynthesis(application.referenceSynthesis, data)"
          :on-click-id-cb="(event: Event, id: string) => openRefDetails(event, id)"
          :on-upload-cb="
            withVersioning(data.id) ? null : (id: string, file: File) => uploadReferenceCsv(id, file)
          "
          :option="data"
          :reference-synthesis="application.referenceSynthesis"
          :repository="data.submission"
          :repository-redirect="(id: string) => showVersioning(id)"
          :show-empty="showInfoEmpty[data.id]"
          :tags="tags"
          class="liste"
        />
      </div>
    </div>
  </PageView>
</template>

<script lang="ts">
import CollapsibleTree from "@/components/common/CollapsibleTree.vue";
import TagsCollapse from "@/components/common/TagsCollapse.vue";
import PageView from "../common/PageView.vue";
import SubMenu from "@/components/common/SubMenu.vue";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import { Button } from "@/types/ui/Button";
import { HttpStatusCodes } from "@/utils/HttpUtils";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import useArray from "@/composable/components/array";
import useBoolean from "@/composable/components/boolean";
import useNumber from "@/composable/components/number";
import useObject from "@/composable/components/object";
import { inject, nextTick, onMounted } from "vue";
import services from "@/composable/services";
import { buildTags } from "@/composable/application/tags";
import { lineCountSynthesis } from "@/composable/application/synthesis";
import {
  computeAuthorizations,
  computeUserPermissions,
  findNodeById,
  hasVersioningStrategy,
} from "@/composable/data/datasManagement";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import ShowErrors from "@/components/application/ShowErrors.vue";
import TitleAndDescription from "@/components/common/TitleAndDescription.vue";
import "@/style/views/data/DatasManagementView.scss";
import i18n from "@/i18n";
import { FileUpload } from "@/composable/data/uploadFile";
import { LocalTag } from "@/types/front/LocalTag";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { LocalNode } from "@/types/front/LocalNode";
import { DefaultValidationCheckResult } from "@/types/generated/UsedGeneratedTypes";

export default {
  name: "DatasManagementView",
  components: {
    TitleAndDescription,
    ShowErrors,
    LoadingAnimate,
    CollapsibleTree,
    TagsCollapse,
    /* DetailsPanel, */
    PageView,
    SubMenu,
  },
  props: {
    applicationName: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    const fileUpload: FileUpload | undefined = inject("fileUpload");
    const { shallowRefArray: datas, doChangeArray: changeDatas } = useArray();
    const { shallowRefArray: subMenuPaths, doChangeArray: changeSubMenuPaths } = useArray();
    const { shallowRefArray: errorsMessages, doChangeArray: changeErrorsMessages } = useArray();
    const { shallowRefArray: errorsList } = useArray();
    const { shallowRefArray: showInfoEmpty } = useArray();
    const { refNumber: currentPage } = useNumber(1);
    const { refBoolean: openPanel, doChangeBoolean: changeOpenPanel } = useBoolean();
    const { refBoolean: isUploading, doChangeBoolean: changeIsUploading } = useBoolean();
    const { refBoolean: isLoading, doChangeBoolean: changeIsLoading } = useBoolean();
    const { reactiveObject: csvRefIsLoading } = useObject();
    const { refBoolean: isLineCountLoading, doChangeBoolean: changeIsLineCountLoading }
      = useBoolean();
    const { refBoolean: canManageRights } = useBoolean();
    const { reactiveObject: chosenRef, doChangeObject: changeChosenRef } = useObject();
    const { reactiveObject: tags, doAddToObject: _addToTags } = useObject();
    const { shallowRefArray: datasToBeShown, doChangeArray: changeDatasToBeShown } = useArray();
    const { reactiveObject: application, doChangeObject: changeApplication } = useObject(
      new LocalApplicationResult(),
    );
    const { refBoolean: canCreateApplication, doChangeBoolean: changeCanCreateApplication }
      = useBoolean(false);
    const { refBoolean: canManagerApplication, doChangeBoolean: changeCanManagerApplication }
      = useBoolean(false);
    const { refBoolean: canManagerUser, doChangeBoolean: changeCanManagerUser } = useBoolean(false);
    const type = window.location.href.split("/");

    const hasTags = useBoolean(false).refBoolean;
    const changeTags = function (tagsToChange: any) {
      _addToTags(tagsToChange);
      hasTags.value
        = tags
          && Object.keys(tags || {}).length !== 0
          && !(Object.keys(tags || {}).length === 1 && Object.keys(tags).includes(LocalTag.NO_TAG_NAME));
      let sortedData: any = application.configuration.hierarchicalNodes
        .map(node => node.nodeName)
        .map((nodeName: string) => datas.value.find(data => data.id === nodeName))
        .filter(node => node)
        .sort(
          (a, b) =>
            application.orderedReferences.indexOf(a.id)
            - application.orderedReferences.indexOf(b.id),
        );
      sortedData = sortedData.map((data: any) => {
        return {
          ...data,
          tags: (data.tags && data.tags.length > 0) ? data.tags : [new LocalTag()],
        };
      });
      changeDatasToBeShown(services.tagService.toBeShown(tags, sortedData));
    };

    function changeAllValueTags(checked: boolean) {
      for (let key in tags) {
        tags[key].selected = checked;
      }
      changeTags(tags);
    }

    function changeTagSelected(payload: { key: string; checked: boolean }) {
      const { key, checked } = payload;
      if (tags[key]) {
        tags[key].selected = checked;
        changeTags(tags);
      }
    }

    onMounted(async () => {
      await init();
      for (let i = 0; i < datas.value.length; i++) {
        showInfoEmpty.value[datas.value[i].id] = { value: false };
      }
      changeSubMenuPaths([
        new SubMenuPath(
          type[type.length - 1] === "dataTypes"
            ? t("dataTypesManagement.data-types").toLowerCase()
            : t("referencesManagement.references").toLowerCase(),
          () => router.push(`/applications/${props.applicationName}/${type[type.length - 1]}`),
          () => router.push(`/applications/${props.applicationName}`),
        ),
      ]);
      changeTags(buildTags(datas.value));
      // @ts-expect-error TODO: pending proper type annotation
      changeDatas(services.tagService.toBeShown(tags, datas.value));
      try {
        const perms = computeUserPermissions(application?.currentApplicationUserRolesResult);
        changeCanCreateApplication(perms.canCreateApplication);
        changeCanManagerApplication(perms.canManagerApplication);
        changeCanManagerUser(perms.canManagerUser);
      }
      catch (error) {
        console.log("missing admin application rights", error);
      }
    });

    function addAuthorizationToData(reference: LocalNode) {
      return computeAuthorizations(reference, application.authorizations as Record<string, Record<string, boolean>>);
    }

    async function init() {
      changeIsLoading(true);
      try {
        if (type[type.length - 1] === "dataTypes") {
          const fetchedApplication = await services.applicationService.getApplication(props.applicationName, [
            "CONFIGURATION",
            "DATATYPE",
            "REFERENCETYPE",
          ]);
          if (!fetchedApplication?.id) {
            return;
          }
          changeApplication(fetchedApplication);
          changeDatas(
            Object.values(services.internationalisationService.treeDataTypesName(application)).map(
              // @ts-expect-error TODO: pending proper type annotation
              addAuthorizationToData,
            ),
          );
          datas.value.map(
            data =>
              (data.submission = application.configuration.dataDescription[data.id].submission),
          );
          canManageRights.value = datas.value.some(dty => dty.isAdmin);
        }
        else {
          const fetchedApplication = await services.applicationService.getApplication(props.applicationName, [
            "CONFIGURATION",
            "DATATYPE",
            "REFERENCETYPE",
          ]);
          if (!fetchedApplication?.id) {
            return;
          }
          changeApplication(fetchedApplication);
          changeDatas(
            Object.values(services.internationalisationService.treeReferenceName(application)).map(
              // @ts-expect-error TODO: pending proper type annotation
              addAuthorizationToData,
            ),
          );
        }
      }
      catch (error) {
        console.error(error);
        services.alertService.toastServerError();
      }
      changeIsLoading(false);
    }

    function buttons(reference: any) {
      return [
        new Button(
          t("referencesManagement.consult"),
          "eye",
          // @ts-expect-error TODO: pending proper type annotation
          (id: string) => consultReference(id),
          "is-dark",
          null,
          !(
            reference.canRead
            && (application.referenceSynthesis.find(
              synthesis => synthesis.referenceType === reference.id,
            )?.lineCount || 0) > 0
          ),
        ),
      ];
    }

    function openRefDetails(event: Event, id: string) {
      event.stopPropagation();
      changeOpenPanel(chosenRef && chosenRef.id === id ? !openPanel.value : true);
      changeChosenRef(findReferenceByid(id));
    }

    function consultReference(id: string) {
      const ref = findReferenceByid(id);
      if (ref) {
        router.push(
          `/applications/${props.applicationName}/${type[type.length - 1]}/${ref.id}`,
        );
      }
    }

    async function uploadReferenceCsv(id: string, refFile: File) {
      fileUpload?.startUpload();
      csvRefIsLoading[id] = true;
      changeIsLineCountLoading(true);
      changeIsUploading(true);
      changeErrorsMessages([]);
      const reference = findReferenceByid(id);
      services.alertService.info(
        i18n.global.t("dataVersioning.modal-dialog-information.title"),
        i18n.global.t("dataVersioning.modal-dialog-information.message-depot"),
        i18n.global.t("dataVersioning.modal-dialog-information.btn-validate"),
        i18n.global.t("dataVersioning.modal-dialog-information.btn-delete"),
        1200,
      );
      try {
        const referenceType = reference.id;
        const result = await services.dataService.addData(
          props.applicationName,
          referenceType,
          refFile,
          null,
          fileUpload?.onUploadProgress,
          fileUpload?.onUploadComplete,
        );
        application.referenceSynthesis = result.referenceSynthesis;
        services.alertService.toastSuccess(t("alert.reference-updated"));
        showInfoEmpty.value[referenceType] = { value: true };
        await nextTick();
      }
      catch (errors) {
        const httpError = errors as { httpResponseCode?: number };
        if (httpError && httpError.httpResponseCode === HttpStatusCodes.PAYLOAD_TOO_LARGE) {
          services.alertService.toastError(t("fileUpload.payloadTooLarge", { max: "100MB" }));
          return;
        }
        await checkMessageErrors(errors, reference.id);
      }
      finally {
        changeIsUploading(false);
        changeIsLineCountLoading(false);
        csvRefIsLoading[id] = false;
        fileUpload?.finishUpload();
      }
    }

    async function checkMessageErrors(errors: string | DefaultValidationCheckResult[] | any, referenceId: string) {
      let componentDescriptions = {};
      if (application?.configuration?.dataDescription?.[referenceId]) {
        componentDescriptions = application.configuration.dataDescription[referenceId]?.componentDescriptions || {};
      }
      if (errors.httpResponseCode === HttpStatusCodes.BAD_REQUEST) {
        errors.content.then((value: any) => {
          for (let i = 0; i < value.length; i++) {
            errorsList.value[i] = value[i];
          }
          if (errorsList.value.length !== 0) {
            // changeTagSelected(errorsList.value);
            changeErrorsMessages(services.errorsService.getCsvErrorsMessages(errorsList.value, componentDescriptions));
          }
          else {
            changeErrorsMessages(
              // @ts-expect-error TODO: pending proper type annotation
              services.errorsService.getErrorsMessages(errors, null, "errors-csv."),
            );
          }
        });
      }
      else {
        services.alertService.toastError(t("alert.reference-csv-upload-error"), errors);
      }
    }

    function findReferenceByid(id: string) {
      return findNodeById(datasToBeShown.value, id);
    }

    function withVersioning(dataId: string) {
      return hasVersioningStrategy(
        application.configuration?.dataDescription as Record<string, { submission?: { strategy?: string } }>,
        dataId,
      );
    }

    function showVersioning(id: string) {
      const data = datas.value.find(dt => dt.id === id);
      router.push(`/applications/${props.applicationName}/dataVersioning/${data.id}`);
    }

    function consultAuthorization() {
      router.push(`/applications/${props.applicationName}/authorizations`);
    }

    return {
      canCreateApplication,
      canManagerApplication,
      canManagerUser,
      openRefDetails,
      uploadReferenceCsv,
      changeTagSelected,
      changeAllValueTags,
      buttons,
      withVersioning,
      showVersioning,
      lineCountSynthesis,
      tags,
      chosenRef,
      application,
      openPanel,
      subMenuPaths,
      datasToBeShown,
      errorsMessages,
      currentPage,
      isUploading,
      isLoading,
      showInfoEmpty,
      isLineCountLoading,
      hasTags,
      type,
      consultAuthorization,
      csvRefIsLoading,
    };
  },
};
</script>
